import type { GameContext } from "../GameContext";

/**
 * Owns the connecting -> in_world -> reconnecting -> in_world/character_select
 * portion of the FSM by listening to NetworkManager's WS lifecycle events
 * (2.8) rather than any scene driving StateManager directly off socket
 * callbacks — keeps that wiring in one place instead of duplicated per
 * in-world scene.
 */
export class WorldController {
  private unsubscribers: Array<() => void> = [];

  constructor(private readonly ctx: GameContext) {}

  /** Call once, right after CharacterSelectController.selectCharacter()
   * has resolved a websocketUrl and state is already "connecting". */
  attachAndConnect(websocketUrl: string): void {
    this.teardown();

    this.unsubscribers.push(
      this.ctx.network.on("ws:open", () => {
        if (this.ctx.state.current === "connecting" || this.ctx.state.current === "reconnecting") {
          this.ctx.state.transition("in_world");
        }
      }),
      this.ctx.network.on("ws:reconnecting", () => {
        if (this.ctx.state.current === "in_world") {
          this.ctx.state.transition("reconnecting");
        }
      }),
      this.ctx.network.on("ws:heartbeat_timeout", () => {
        // Mirrors the server's own "a connection that misses heartbeat is
        // dropped" behavior (2.8) — the client treats a missed pong as
        // equivalent to a dropped connection and starts its own recovery
        // path rather than waiting for the server to notice first.
        if (this.ctx.state.current === "in_world") {
          this.ctx.state.transition("reconnecting");
        }
      })
    );

    this.ctx.network.connect(websocketUrl);
  }

  /** Give up on reconnecting and fall back to character select. */
  abandonConnection(): void {
    this.ctx.network.disconnect();
    if (this.ctx.state.current === "reconnecting" || this.ctx.state.current === "in_world") {
      this.ctx.state.transition("character_select");
    }
    this.teardown();
  }

  teardown(): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
  }
}
